import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AssignmentsService } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableSortHeader,
  TableRow,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { formatDuration } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { PROVIDER_TIMECARDS_LIST_QUERY_KEY } from './queryKeys';
import { TimecardCard } from './TimecardCard';
import { TimecardDrawer } from './TimecardDrawer';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20;

const QUEUE_CATEGORY = 'ProviderTimecardQueueNames';

type ApiParams = Parameters<typeof AssignmentsService.assignmentsTimecardList>[0];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const TimecardsList: React.FC = () => {
  useDocumentTitle('Timecards');
  const { formatCurrencyPostfix } = useCurrencyFormatter({
    postfix: '',
    placeholder: '-',
    showPlaceholderForZero: true,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const displayTypePrefKey = 'ProviderTimecards';
  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];

  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      displayType,
    ]
  );

  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) {
      newSearchParams.set('queue', queueCode);
    } else {
      newSearchParams.delete('queue');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  const queryKey = useMemo(
    () => [
      PROVIDER_TIMECARDS_LIST_QUERY_KEY,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      ordering,
    ],
    [selectedQueueCode, search, statuses, reasons, ordering]
  );

  const {
    data,
    isLoading: isLoadingTimecards,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return AssignmentsService.assignmentsTimecardList({
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
    keepPreviousData: true,
    staleTime: 30_000,
    enabled: true,
  });

  const timecards = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreTimecards = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreTimecards, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingTimecards,
  });

  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setIsDrawerOpen(true);
    } else {
      if (isDrawerOpen) {
        setIsDrawerOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (timecardId: string) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', timecardId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    newSearchParams.delete('tab');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedIdFromUrl = searchParams.get('id');

  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    return timecards.findIndex((t) => String(t.id) === String(selectedIdFromUrl));
  }, [timecards, selectedIdFromUrl]);

  const prevTimecardId: string | null = useMemo(() => {
    if (selectedIndex > 0) return timecards[selectedIndex - 1]?.id ?? null;
    return null;
  }, [timecards, selectedIndex]);

  const nextTimecardId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < timecards.length - 1)
      return timecards[selectedIndex + 1]?.id ?? null;
    return null;
  }, [timecards, selectedIndex]);

  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevTimecardId ?? null,
        next: nextTimecardId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevTimecardId, nextTimecardId]);

  const cachedNeighbors = selectedIdFromUrl
    ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
    : undefined;
  const drawerPrevId: string | null =
    selectedIndex >= 0 ? prevTimecardId : (cachedNeighbors?.prev ?? null);
  const drawerNextId: string | null =
    selectedIndex >= 0 ? nextTimecardId : (cachedNeighbors?.next ?? null);

  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  useEffect(() => {
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;
    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) return;

    if (selectedIndex === -1 && !isLoadingTimecards) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('id');
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingTimecards,
    searchParams,
    setSearchParams,
  ]);

  return (
    <>
      <div
        className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden"
        data-testid="timecards-list"
      >
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="far fa-fw fa-clock tw:mr-1" />
                  Timecards
                </Heading>
              </div>

              <div className="tw:mt-4">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.TimeCard}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  priorityQueueList={[QueueCodes.NEEDS_RESPONSE]}
                  onQueueChange={handleQueueChange}
                />
              </div>
            </div>

            <div className="tw:mt-1">
              <div className="tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.TimeCard}
                  showStatus={true}
                  showReason={false}
                  showDisplayType={true}
                  sortOptions={[
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__location__name' },
                    { label: 'Status', value: 'current_status__title' },
                    { label: 'Date Range', value: 'start_on' },
                    { label: 'Total Amt', value: 'total_payable_amount' },
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Last Status Change', value: 'status_at' },
                    { label: 'Created', value: 'created_at' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>

            <div className="tw:mt-6">
              {isError ? (
                <ListErrorState testId="timecards-error" />
              ) : isLoadingTimecards && timecards.length === 0 ? (
                <div className="tw:text-center tw:py-8" data-testid="timecards-loading">
                  Loading timecards...
                </div>
              ) : timecards.length === 0 ? (
                <div
                  className="tw:text-center tw:text-zinc-500 tw:py-8"
                  data-testid="timecards-empty"
                >
                  No timecards found
                  <div className="tw:italic tw:text-zinc-500 tw:text-sm">
                    Refine search criteria or check back later
                  </div>
                </div>
              ) : (
                <>
                  {timecards.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {timecards.length} of {totalCount} timecards
                    </div>
                  )}
                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {timecards.map((timecard) => {
                          const isSelected = timecard.id === selectedIdFromUrl;
                          return (
                            <button
                              key={timecard.id}
                              type="button"
                              onClick={() => handleRowClick(timecard.id)}
                              className="tw:cursor-pointer tw:block tw:w-full tw:text-left"
                              data-testid={`timecard-card-${timecard.id}`}
                            >
                              <TimecardCard timecard={timecard} isSelected={isSelected} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <Table striped dense>
                      <TableHead>
                        <TableHeader>Code</TableHeader>
                        <TableSortHeader
                          sortKey="facility__name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Facility
                        </TableSortHeader>
                        <TableHeader>Location</TableHeader>
                        <TableSortHeader
                          sortKey="current_status__title"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Status
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="start_on"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Date Range
                        </TableSortHeader>
                        <TableHeader># Shifts</TableHeader>
                        <TableHeader>Duration</TableHeader>
                        <TableSortHeader
                          sortKey="total_payable_amount"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                          className="tw:!text-right"
                        >
                          Total Amt
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="updated_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Updated
                        </TableSortHeader>
                      </TableHead>
                      <TableBody>
                        {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                        {timecards.map((timecard) => {
                          const isSelected = timecard.id === selectedIdFromUrl;
                          const dateRange = [
                            timecard.start_on
                              ? format(parseISO(timecard.start_on), DATE_FORMAT)
                              : null,
                            timecard.end_on ? format(parseISO(timecard.end_on), DATE_FORMAT) : null,
                          ]
                            .filter(Boolean)
                            .join(' - ');

                          return (
                            <TableRow
                              key={timecard.id}
                              onClick={() => handleRowClick(timecard.id)}
                              className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              data-testid={`timecard-row-${timecard.id}`}
                            >
                              <TableCell className="tw:font-mono tw:text-xs">
                                {timecard.code}
                              </TableCell>
                              <TableCell>{timecard.facility.name}</TableCell>
                              <TableCell>
                                {getFacilityLocationDisplayValue(timecard.facility) || '--'}
                              </TableCell>
                              <TableCell>
                                <StatusPill
                                  scope={WorkflowScopes.TimeCard}
                                  status={timecard.current_status}
                                />
                              </TableCell>
                              <TableCell>{dateRange || '--'}</TableCell>
                              <TableCell>
                                {timecard.shift_count}{' '}
                                {timecard.shift_count === 1 ? 'shift' : 'shifts'}
                              </TableCell>
                              <TableCell>
                                {timecard.total_duration
                                  ? formatDuration(timecard.total_duration)
                                  : '--'}
                              </TableCell>
                              <TableCell className="tw:text-right">
                                {formatCurrencyPostfix(timecard.total_amount)}
                              </TableCell>
                              <TableCell>
                                {timecard.updated_at
                                  ? format(parseISO(timecard.updated_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more timecards...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <TimecardDrawer
                key={selectedIdFromUrl}
                timecardId={selectedIdFromUrl}
                prevId={drawerPrevId}
                nextId={drawerNextId}
                selectedQueueCode={selectedQueueCode}
                onClose={handleCloseDrawer}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
